import { getParsedQuery, arrayValueToQueryString, valueToQueryString } from './queryString';

/**
 * 범용 필터 유틸리티
 * 
 * 어떤 페이지의 필터든 필터 정의만 있으면 자동으로 처리합니다.
 */

/**
 * 필터 값 타입 열거형
 * 
 * 각 필터의 데이터 타입을 명확히 정의합니다.
 */
export enum FilterValueType {
  NUMBER_ARRAY = 'number[]',
  STRING_ARRAY = 'string[]',
  NUMBER = 'number',
  STRING = 'string',
  BOOLEAN = 'boolean',
}

/**
 * 필터 정의 인터페이스
 * 
 * 각 필터의 설정을 정의하는 객체입니다.
 * 이 설정을 기반으로 자동으로 초기값, 파싱, 변환 등이 처리됩니다.
 */
export interface FilterDefinition {
  /** 필터의 고유 키 (camelCase 권장) */
  key: string;
  
  /** 필터 값의 타입 */
  type: FilterValueType;
  
  /** 필터의 기본값 */
  defaultValue: any;
  
  /** 
   * API 호출 시 사용할 키 이름
   * 
   * 예: keyword 필터는 API에서 'searchKeyword'로 전송되어야 하는 경우
   * ```typescript
   * keyword: {
   *   key: 'keyword',
   *   apiKey: 'searchKeyword', // API에서는 'searchKeyword'로 전송
   * }
   * ```
   */
  apiKey?: string;
  
}

export type FilterConfig = Record<string, FilterDefinition>;

/**
 * 배열 타입 필터 키 목록 생성
 */
export const generateArrayValueKeys = (filters: FilterConfig): string[] => {
  return Object.values(filters)
    .filter(filter => filter.type.endsWith('[]'))
    .map(filter => filter.key);
};


/**
 * 초기 검색 파라미터 생성
 */
export const generateInitialSearchParams = (filters: FilterConfig): Record<string, any> => {
  const params: Record<string, any> = {};
  Object.values(filters).forEach(filter => {
    params[filter.key] = filter.defaultValue;
  });
  return params;
};

/**
 * FilterForm 생성
 */
export const generateFilterForm = <T = Record<string, any>>(
  filters: FilterConfig,
  searchParams: Record<string, any>,
): T => {
  const form: Record<string, any> = {};
  Object.values(filters).forEach(filter => {
    form[filter.key] = searchParams[filter.key];
  });
  return form as T;
};

/**
 * URL 쿼리 스트링을 SearchParams로 변환
 * 
 * @param filters 필터 설정
 * @param queryString URL 쿼리 스트링
 * @param customParser 커스텀 파싱 함수 (선택사항)
 */
export const parseSearchParams = <T = Record<string, any>>(
  filters: FilterConfig,
  queryString: string,
  customParser?: (key: string, parsedQuery: Record<string, string[]>, defaultValue: any) => any,
): T => {
  const parsedQuery = getParsedQuery(queryString);
  const result: Record<string, any> = {};

  Object.entries(filters).forEach(([key, config]) => {
    const queryValue = parsedQuery[key];
    const defaultValue = config.defaultValue;

    // 커스텀 파서가 있으면 우선 사용
    if (customParser) {
      const customValue = customParser(key, parsedQuery, defaultValue);
      if (customValue !== undefined) {
        result[key] = customValue;
        return;
      }
    }

    // 쿼리 값이 없으면 기본값 사용
    if (!queryValue) {
      result[key] = defaultValue;
      return;
    }

    // 타입에 따라 자동 파싱
    switch (config.type) {
      case FilterValueType.NUMBER_ARRAY:
        result[key] = queryValue.map((v: string) => Number(v));
        break;
      case FilterValueType.STRING_ARRAY:
        result[key] = queryValue;
        break;
      case FilterValueType.NUMBER:
        result[key] = Number(queryValue[0]);
        break;
      case FilterValueType.BOOLEAN:
        result[key] = JSON.parse(queryValue[0]);
        break;
      case FilterValueType.STRING:
      default:
        result[key] = queryValue[0];
        break;
    }
  });

  return result as T;
};